import { useState } from 'react'

import { Dialog, Field, Text, TextField } from '@loadsmart/miranda-react'

import { type Thread } from 'assistant/domain/Thread'
import { validateTitle } from 'assistant/domain/threadTitle'

type RenameThreadDialogProps = {
  thread: Thread
  onSave: (title: string) => Promise<void>
  onClose: () => void
}

export default function RenameThreadDialog({ thread, onSave, onClose }: RenameThreadDialogProps) {
  const [value, setValue] = useState(thread.title)
  const [message, setMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  async function save() {
    const result = validateTitle(value)

    if (!result.ok) {
      setMessage(result.reason)
      return
    }

    setMessage('')
    setIsSaving(true)

    try {
      await onSave(result.title)
      onClose()
    } catch {
      setMessage("Couldn't rename this conversation. Try again.")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open size="small" variant="neutral" onClose={onClose}>
      <Dialog.Header>
        Rename conversation
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        <Field>
          <Field.Label id="renameThreadLabel">Name</Field.Label>
          <TextField
            aria-label="Conversation name"
            value={value}
            onInput={event => {
              setValue((event.target as unknown as HTMLInputElement).value)
              setMessage('')
            }}
          />
        </Field>
        {message && (
          <Text variant="body-sm" color="color-danger-60" role="alert">
            {message}
          </Text>
        )}
      </Dialog.Body>
      <Dialog.Footer />
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onClose}>Cancel</Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={save} loading={isSaving}>
          Save
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
